/**
 * HubSection - Collapsible hub section showing pages with access controls
 */

import { useState, useMemo } from 'react'
import { ChevronDown, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { HubDefinition } from '../../lib/hubRegistry'
import type { NavAccessLevel } from '../types'
import { PageAccessRow } from './PageAccessRow'

interface HubSectionProps {
  hub: HubDefinition
  pages: Array<{ route: string; label: string; accessLevel: NavAccessLevel; premiumOnly?: boolean }>
  pageOverrides: Record<string, boolean>
  canManage: boolean
  onPageToggle: (route: string) => void
}

export function HubSection({ hub, pages, pageOverrides, canManage, onPageToggle }: HubSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const stats = useMemo(() => {
    const total = pages.length
    const allowed = pages.filter((p) => pageOverrides[p.route] !== false).length
    return { total, allowed, blocked: total - allowed }
  }, [pages, pageOverrides])

  const HubIcon = hub.icon

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 transition-colors',
          'bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800/50',
          'hover:from-slate-100 dark:hover:from-slate-700'
        )}
      >
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br', hub.accentGradient)}>
          <HubIcon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{hub.name}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">{stats.allowed} of {stats.total} pages accessible</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />{stats.allowed}
          </span>
          {stats.blocked > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
              <XCircle className="w-3 h-3" />{stats.blocked}
            </span>
          )}
        </div>
        <ChevronDown className={cn('w-4 h-4 text-slate-400 transition-transform', !isExpanded && '-rotate-90')} />
      </button>
      {isExpanded && (
        <div className="p-3 space-y-1.5 bg-slate-50/50 dark:bg-slate-900/30">
          {pages.map((page) => (
            <PageAccessRow key={page.route} page={page} isAllowed={pageOverrides[page.route] !== false} canManage={canManage} onToggle={() => onPageToggle(page.route)} />
          ))}
        </div>
      )}
    </div>
  )
}
